import { IModel, Collection, IAttribute, IViolation } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Activity } from './activity';

import { Dataset } from 'app/model/dataset/dataset';
import { ParameterValue } from 'app/model/base/parameter-value';
import { validateEntityParameters, validateManagedVNetIRStatus, validatePathInStagingAndLoggingSettings } from 'app/shared/utils/activity-validation.util';
import { StagingSettings } from './staging-settings';
import { Owned } from '../base/named';
import { ValueAdapter } from '../adapters/value.adapter';
import { format } from 'app/shared/utils/string.util';
import { StoreReadSettings } from './copysource/store-read-settings/store-read-settings';
import {
    azureStorageAuthTypes,
    FormatDatasetV2,
    IValidationContext,
    ModelConstants,
    validateLinkedServicePermission
} from 'shell-models-lib';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { AzureBlobStorageLinkedService } from '../linkedservice/azure-blob-storage.linkedservice';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { LakehouseLocation } from '../dataset/location/lakehouse.location';

@Metamodel.model({
    name: ModelConstants.deleteActivity,
    display: [
        format(stringResources.ModelName_ActivityTemplate, stringResources.Delete),
        format(stringResources.ModelName_ActivitiesTemplate, stringResources.Delete)
    ],
    referable: true
})
export class DeleteActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly RECURSIVE: IAttribute;
    public static readonly ENABLE_LOGGING: IAttribute;
    public static readonly MAXCONCURRENT_CONNECTIONS: IAttribute;

    @Metamodel.attribute({
        display: stringResources.Recursive_Delete_Label,
        association: 'RECURSIVE',
        templatable: true
    })
    public recursive?: boolean;

    @Metamodel.attribute({
        display: !__IS_FABRIC_DPE__ ? stringResources.ModelName_DataSet : stringResources.ConnectionLabel,
        constraint: {
            required: true,
            validator: DeleteActivity.validateDataset
        },
        reference: (context) => !(context?.inlineDataset),
        type: Dataset.MODEL
    })
    public dataset: Dataset;

    /**
     * Parameter value for referenced dataset.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false,
            validator: DeleteActivity.validateDatasetParameters
        }
    })
    public datasetParameters: ParameterValue[] = [];

    @Metamodel.attribute({
        templatable: true,
        association: 'MAXCONCURRENT_CONNECTIONS',
        constraint: {
            minimum: 1,
            maximum: 10
        },
        display: stringResources.ModelName_MaxConcurrentConnections
    })
    public maxConcurrentConnections?: number;

    @Metamodel.attribute({
        templatable: true,
        association: 'ENABLE_LOGGING',
        display: stringResources.DeleteLoggingSetting_EnableLabel
    })
    public enableLogging?: boolean = true;

    @Metamodel.attribute({
        display: stringResources.LoggingSettingsLabel,
        type: StagingSettings.MODEL,
        constraint: {
            validator: DeleteActivity.loggingAccountInDeleteActivityValidator
        }
    })
    public logStorageSettings: StagingSettings = Owned.create(StagingSettings.MODEL, this);

    // for dataset v2
    @Metamodel.attribute({
        display: stringResources.ModelName_StoreReadSettings,
        type: StoreReadSettings.MODEL
    })
    public storeSettings: StoreReadSettings;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    public static validateDatasetParameters(activity: DeleteActivity): IViolation[] | undefined {
        if (activity != null && activity.dataset != null) {
            return validateEntityParameters(activity.dataset, activity.datasetParameters || [], activity);
        }
        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static async validateDataset(activity: DeleteActivity, _value: any, _violations: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        let violations: IViolation[] = [];

        if (activity.dataset?.linkedService) {
            const linkedServiceType = activity.dataset.getLinkedServiceType();
            const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(context.contextData, 'LinkedServiceRegistryService');
            const metadata = linkedServiceRegistry.getLinkedServiceConfiguration(linkedServiceType);
            if (metadata && !!metadata.notSupportDeleteActivity) {
                violations.push({
                    message: () => stringResources.LinkedService_NotSupportDelete_ErrorMessage
                });
            }
            if (activity.dataset.linkedService instanceof AzureBlobStorageLinkedService &&
                activity.dataset.linkedService.authenticationMethod === azureStorageAuthTypes.Anonymous) {
                violations.push({
                    message: () => format(stringResources.Anonymous_BlobStorage_ErrorTemplate, format(stringResources.ModelName_ActivityTemplate, stringResources.Delete))
                });
            }
            if (linkedServiceType === adfModelTypeMap.lakehouse.linkedServiceType && activity.dataset instanceof FormatDatasetV2 && activity.dataset.location) {
                const location = activity.dataset.location as LakehouseLocation;
                const folder = location.getFolderPath() ?? ValueAdapter.getTemplatedValue(location, LakehouseLocation.FOLDER_PATH);
                const file = location.getFileName() ?? ValueAdapter.getTemplatedValue(location, LakehouseLocation.FILE_NAME);
                if (!folder && !file) {
                    violations.push({
                        message: () => format(stringResources.Lakehouse_DeleteRoot_Message)
                    });
                }
            }
        }

        const irStatusViolations = await validateManagedVNetIRStatus(context, activity.dataset);
        if (irStatusViolations != null) {
            violations = violations.concat(irStatusViolations);
        }

        return violations;
    }

    private static loggingAccountInDeleteActivityValidator(activity: DeleteActivity, value?: StagingSettings, violation?: IViolation, context?: IValidationContext): IViolation[] | undefined {
        const result: IViolation[] = [];
        // If enableLogging is checked but logging linked service is not selected
        if ((activity.enableLogging || ValueAdapter.isTemplated(activity, DeleteActivity.ENABLE_LOGGING)) && activity.logStorageSettings != null) {
            if (activity.logStorageSettings.storageLinkedService == null) {
                result.push({
                    context: activity,
                    message: () => format(stringResources.DeleteLoggingSetting_ErrorMessage_MissingLinkedService,
                        __IS_FABRIC_DPE__ ? stringResources.ConnectionLabel.toLocaleLowerCase() : stringResources.LinkedServiceLowerCase)
                });
            } else {
                if (activity.logStorageSettings.storageLinkedService instanceof AzureBlobStorageLinkedService &&
                    activity.logStorageSettings.storageLinkedService.authenticationMethod === azureStorageAuthTypes.Anonymous) {
                    result.push({
                        context: activity,
                        message: () => format(stringResources.Anonymous_BlobStorage_ErrorTemplate, stringResources.LoggingSettingsLabel.toLocaleLowerCase())
                    });
                } else if (activity.logStorageSettings.storageLinkedService) {
                    result.push(...validateLinkedServicePermission(activity.logStorageSettings.storageLinkedService, context));
                }
                result.push(...validatePathInStagingAndLoggingSettings(activity.logStorageSettings));
            }
        }
        return result;
    }

    public onActivityCloned(clonedActivity: DeleteActivity, context: { inlineDataset?: boolean }): void {
        if (this.dataset != null) {
            this.dataset.onDatasetCloned(clonedActivity.dataset, context);
        }
    }
}
